import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  PlusIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PlayCircleIcon,
  CheckIcon,
  ScissorsIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import SparkleIcon from '../SparkleIcon';
import { ThinkingIndicator } from '../ThinkingIndicator';
import { CollapsibleMarkdown } from './CollapsibleMarkdown';
import { SnipModal } from './SnipModal';
import { useVideoChat, VideoChatAttachment } from '../../hooks/useVideoChat';

interface VideoChatPanelProps {
  /** The lecture being watched — scopes the conversation list + every turn. */
  videoId: string;
  /** Fresh playhead (seconds) read at send time — the agent's deixis anchor. */
  getPlayhead: () => number;
  /** Seek the player when a [m:ss] pill or moment card is clicked. */
  onSeek: (sec: number) => void;
  /** Title of the section at the current playhead — drives the dynamic prompts. */
  currentSection: string | null;
  /** Capture the paused frame as a data URL. Uploads grab silently; YouTube
   *  goes through the browser's share-this-tab prompt (Screen Capture API).
   *  Resolves null when the user dismisses the prompt / capture fails. */
  captureFrame?: () => Promise<string | null>;
  /** Pin an AI answer to the ThoughtPad (saves a chat_answer note + jumps tabs).
   *  Absent → the "Add to ThoughtPad" button is hidden. */
  onPinAnswer?: (messageId: string, text: string) => Promise<void>;
}

// Memoized: VideoWatch re-renders ~4x/sec during playback (timeupdate →
// setCurrentTime). With stable props this panel — and its markdown/KaTeX/
// figure subtree — must NOT re-render on that clock, or answers visibly
// flicker while the video plays.
export const VideoChatPanel: React.FC<VideoChatPanelProps> = React.memo(({ videoId, getPlayhead, onSeek, currentSection, captureFrame, onPinAnswer }) => {
  const navigate = useNavigate();
  const {
    messages,
    sessions,
    activeSessionId,
    isStreaming,
    streamingMessageId,
    agentStatus,
    error,
    sendMessage,
    createNewSession,
    selectSession,
    stopStreaming,
  } = useVideoChat(videoId);

  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<VideoChatAttachment[]>([]);
  const [showSessionMenu, setShowSessionMenu] = useState(false);
  const [snipImage, setSnipImage] = useState<string | null>(null);
  const [snipError, setSnipError] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());

  const handlePin = async (messageId: string, text: string) => {
    if (!onPinAnswer) return;
    try {
      await onPinAnswer(messageId, text);
      setPinnedIds((prev) => new Set(prev).add(messageId));
    } catch { /* VideoWatch surfaces the error (incl. not-linked) */ }
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Snip: pause + grab the frame (VideoWatch), then region-select in SnipModal.
  const handleSnip = async () => {
    if (!captureFrame) return;
    const frame = await captureFrame();
    if (!frame) {
      setSnipError(true);
      setTimeout(() => setSnipError(false), 4000);
      return;
    }
    setSnipImage(frame);
  };

  const handleSnipConfirm = (dataUrl: string) => {
    setSnipImage(null);
    const sec = getPlayhead();
    setAttachments((prev) => prev.length >= 5 ? prev : [...prev, {
      type: 'image',
      data_url: dataUrl,
      file_name: `frame-${Math.floor(sec / 60)}m${String(sec % 60).padStart(2, '0')}s.jpg`,
      mime_type: 'image/jpeg',
    }]);
    inputRef.current?.focus();
  };

  // Photographed problem/diagram → base64 data URLs, same shape general chat
  // sends; the backend's image_normalizer reads them via the image_reader agent.
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    Array.from(e.target.files || []).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) {
          setAttachments((prev) => prev.length >= 5 ? prev : [...prev, {
            type: 'image', data_url: dataUrl, file_name: file.name, mime_type: file.type,
          }]);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = ''; // allow re-selecting the same file
  };

  // "Thinking" = streaming but the answer hasn't started → the flower loader.
  const streamingMsg = messages.find((m) => m.id === streamingMessageId);
  const isThinking = isStreaming && !streamingMsg?.content_text;
  const hasMessages = messages.length > 0;
  const activeSession = sessions.find((s) => s.id === activeSessionId);

  // Starter prompts — concrete and useful for a paused lecture; the first is
  // dynamic to the section the student is on. Clicking fills the input to edit.
  const starters = [
    'Explain this part simply',
    'Summarise the key points so far',
    'Walk me through an example',
    'What should I remember here?',
  ];

  // Auto-scroll: instant while streaming, smooth once it settles.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: isStreaming ? 'auto' : 'smooth' });
  }, [messages, isThinking, isStreaming]);

  // Auto-grow the textarea.
  useEffect(() => {
    const el = inputRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 160)}px`; }
  }, [input]);

  const submit = () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || isStreaming) return;
    const atts = attachments;
    setInput('');
    setAttachments([]);
    void sendMessage(text, getPlayhead(), atts);
  };

  // Book page-links / note-links can appear in a video answer (the book bucket).
  const handlePageLinkClick = (bookId: string, pageNumber: number) => navigate(`/dhyana/${bookId}?page=${pageNumber}`);
  const handleNoteClick = (noteId: string) => navigate(`/note/${noteId}`);

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {/* Chat sessions header — title dropdown + session count (mirrors the reader) */}
      <div className="flex-shrink-0 border-b border-slate-200 p-3 dark:border-slate-700/50">
        <div className="flex items-center justify-between gap-2">
          <div className="relative flex flex-1 items-center">
            <button
              onClick={(e) => { e.stopPropagation(); setShowSessionMenu((v) => !v); }}
              className="flex flex-1 items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-[#1F2937] transition-colors hover:bg-[#64748B]/10 dark:text-[#E2E8F0] dark:hover:bg-[#06B6D4]/10"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 flex-shrink-0 text-cyan-600 dark:text-[#06B6D4]" />
              <span className="flex-1 truncate text-left">{activeSession?.title || 'Lecture Q&A'}</span>
              <ChevronDownIcon className={`h-3 w-3 flex-shrink-0 transition-transform ${showSessionMenu ? 'rotate-180' : ''}`} />
            </button>

            {showSessionMenu && (
              <>
                <div className="fixed inset-0 z-0" onClick={() => setShowSessionMenu(false)} />
                <div className="glass-pane absolute left-0 top-full z-10 mt-1 flex max-h-[360px] w-56 flex-col rounded-lg shadow-xl">
                  <div className="flex-1 overflow-y-auto p-1 scrollbar-slim">
                    {sessions.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-[#6B7280] dark:text-[#94A3B8]">No conversations yet</div>
                    ) : (
                      sessions.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => { void selectSession(s.id); setShowSessionMenu(false); }}
                          className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                            s.id === activeSessionId
                              ? 'bg-[#64748B]/20 text-[#1F2937] dark:bg-[#06B6D4]/20 dark:text-[#E2E8F0]'
                              : 'text-[#6B7280] hover:bg-[#64748B]/10 dark:text-[#94A3B8] dark:hover:bg-[#06B6D4]/10'
                          }`}
                        >
                          <div className="truncate font-medium">{s.title || 'New conversation'}</div>
                        </button>
                      ))
                    )}
                  </div>
                  <div className="flex-shrink-0 border-t border-slate-200/70 p-1 dark:border-white/10">
                    <button
                      onClick={() => { createNewSession(); setShowSessionMenu(false); }}
                      className="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-cyan-600 transition-colors hover:bg-[#64748B]/10 dark:text-[#06B6D4] dark:hover:bg-[#06B6D4]/10"
                    >
                      <span className="flex items-center gap-2"><PlusIcon className="h-4 w-4" />New chat</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <span className="flex-shrink-0 text-xs text-[#6B7280] dark:text-[#94A3B8]">
            {sessions.length} session{sessions.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto scrollbar-slim px-3 py-4">
        {!hasMessages && !isThinking ? (
          <div className="flex h-full items-center justify-center px-6 text-center">
            <div>
              <div className="mx-auto mb-3 w-fit"><SparkleIcon size={48} color="#06B6D4" animated /></div>
              <p className="font-semibold text-[#1F2937] dark:text-[#E2E8F0]">Clarity begins with a question. Ask away.</p>
            </div>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id}>
              {m.message_type === 'user' ? (
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-xl rounded-br-sm bg-cyan-600 px-3 py-2 text-sm font-medium text-white shadow-sm dark:bg-[#06B6D4]">
                    {(m.attachments?.length ?? 0) > 0 && (
                      <div className="mb-1.5 flex flex-wrap gap-1.5">
                        {m.attachments!.map((a: any, i: number) => (
                          <img
                            key={i}
                            src={a.data_url}
                            alt={a.file_name || 'attachment'}
                            onClick={() => setZoomedImage(a.data_url)}
                            className="h-20 w-20 cursor-zoom-in rounded-lg border border-white/30 object-cover transition hover:opacity-90"
                          />
                        ))}
                      </div>
                    )}
                    {m.content_text}
                  </div>
                </div>
              ) : (
                // Skip empty AI placeholders — the ThinkingIndicator below covers loading.
                m.content_text ? (
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center">
                      {/* animated always — matches Dhyana's "live" avatar */}
                      <SparkleIcon size={22} color="#06B6D4" animated />
                    </span>
                    <div className="min-w-0 flex-1 rounded-xl rounded-tl-sm border border-slate-200/80 bg-white px-3 py-2 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
                      <CollapsibleMarkdown
                        content={m.content_text}
                        className="text-sm text-slate-800 dark:text-[#E2E8F0]"
                        isStreaming={m.id === streamingMessageId}
                        onPageLinkClick={handlePageLinkClick}
                        onNoteClick={handleNoteClick}
                        onTimestampClick={onSeek}
                      />
                      {m.id === streamingMessageId && (
                        <span className="ml-0.5 inline-flex gap-1 align-middle">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cyan-500" style={{ animationDelay: '0ms' }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cyan-500" style={{ animationDelay: '150ms' }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cyan-500" style={{ animationDelay: '300ms' }} />
                        </span>
                      )}
                      {/* Pin to ThoughtPad — only on a settled answer (mirrors Dhyana) */}
                      {onPinAnswer && m.id !== streamingMessageId && !m.id.startsWith('temp-') && (
                        <div className="mt-2 border-t border-slate-100 pt-2 dark:border-slate-700/50">
                          <button
                            onClick={() => handlePin(m.id, m.content_text)}
                            disabled={pinnedIds.has(m.id)}
                            className="flex items-center gap-1.5 text-xs font-semibold text-cyan-600 transition-opacity hover:opacity-80 disabled:opacity-60 dark:text-[#06B6D4]"
                            title="Save this answer to your ThoughtPad"
                          >
                            {pinnedIds.has(m.id) ? (
                              <><CheckIcon className="h-3.5 w-3.5" /> Added to ThoughtPad</>
                            ) : (
                              <><PlusIcon className="h-3.5 w-3.5" /> Add to ThoughtPad</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null
              )}
            </div>
          ))
        )}

        {isThinking && <ThinkingIndicator agentStatus={agentStatus} />}

        {error && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-slate-200 p-3 dark:border-slate-700/50">
        {/* Contextual prompts — full-width card above the input (mirrors the reader) */}
        {!hasMessages && !isThinking && (
          <div className="mb-3 rounded-xl border border-slate-200/50 bg-slate-50/80 p-3 backdrop-blur-sm dark:border-slate-700/30 dark:bg-slate-800/30">
            {currentSection && (
              <div className="mb-2.5 flex items-start gap-2">
                <PlayCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-500 dark:text-cyan-400" />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Now playing<span className="text-slate-400 dark:text-slate-500">{' : '}</span>
                  <span className="font-medium text-cyan-600 dark:text-cyan-400">{currentSection}</span>
                </span>
              </div>
            )}
            <div className="space-y-2">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Try asking</span>
              <div className="flex flex-wrap gap-2">
                {starters.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setInput(s); inputRef.current?.focus(); }}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-sm transition-all hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 hover:shadow dark:border-slate-600/50 dark:bg-slate-700/50 dark:text-slate-300 dark:hover:border-cyan-600/50 dark:hover:bg-cyan-900/20 dark:hover:text-cyan-300"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {snipError && (
          <div className="mb-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            Frame capture didn't complete — if your browser asked to share this tab, allow it and snip again (or upload a screenshot).
          </div>
        )}
        {/* Pending attachment previews — thumbnails with remove, above the input */}
        {attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {attachments.map((a, i) => (
              <div key={i} className="group relative">
                <img
                  src={a.data_url}
                  alt={a.file_name}
                  title={a.file_name}
                  onClick={() => setZoomedImage(a.data_url)}
                  className="h-16 w-16 cursor-zoom-in rounded-lg border border-slate-200 object-cover shadow-sm transition hover:opacity-90 dark:border-slate-700"
                />
                <button
                  onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                  aria-label={`Remove ${a.file_name}`}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white opacity-0 shadow transition-opacity group-hover:opacity-100 dark:bg-slate-600"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />
        {/* Composer — mirrors Dhyana: textarea with floating top-right actions.
            pr-44 reserves room for up to 4 icons (snip · clip · clear · send) so
            text never runs under them. */}
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
              else if (e.key === 'Escape') { e.preventDefault(); setInput(''); }
            }}
            rows={1}
            placeholder="Ask about this lecture…"
            className="w-full resize-none overflow-y-auto rounded-xl border border-slate-200 bg-white p-3.5 pr-44 text-sm leading-relaxed text-slate-800 placeholder-slate-400 shadow-sm transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 dark:border-slate-700 dark:bg-slate-800/50 dark:text-[#E2E8F0] dark:placeholder-[#94A3B8] dark:focus:border-[#06B6D4] dark:focus:ring-[#06B6D4]/30"
            style={{ minHeight: '120px', maxHeight: '260px' }}
          />
          <div className="absolute right-3 top-3 flex items-center gap-2">
            {input.trim() && !isStreaming && (
              <button
                onClick={() => { setInput(''); inputRef.current?.focus(); }}
                title="Clear (Esc)"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
            {captureFrame && (
              <button
                onClick={handleSnip}
                disabled={isStreaming || attachments.length >= 5}
                title="Snip the paused frame — select the part you're asking about"
                className="rounded-lg p-2 text-cyan-600 transition-colors hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-[#06B6D4] dark:hover:bg-[#06B6D4]/10"
              >
                <ScissorsIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming || attachments.length >= 5}
              title="Upload an image — a photographed question, a diagram…"
              className="rounded-lg p-2 text-cyan-600 transition-colors hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-[#06B6D4] dark:hover:bg-[#06B6D4]/10"
            >
              <PaperClipIcon className="h-5 w-5" />
            </button>
            {isStreaming ? (
              <button
                onClick={stopStreaming}
                title="Stop generating"
                className="rounded-lg bg-slate-200 p-2 text-slate-600 shadow-sm transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
              >
                <StopIcon className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={!input.trim() && attachments.length === 0}
                title="Send"
                className="rounded-lg p-2 text-white shadow-sm transition hover:opacity-90 hover:shadow-glow-cyan disabled:cursor-not-allowed disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg,#06B6D4,#3B82F6)' }}
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {snipImage && (
        <SnipModal
          imageUrl={snipImage}
          onConfirm={handleSnipConfirm}
          onClose={() => setSnipImage(null)}
        />
      )}

      {/* Image zoom — same overlay pattern as Dhyana's zoomedImageUrl */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute -top-12 right-0 flex items-center gap-2 text-white transition-colors hover:text-gray-300"
            >
              <span className="text-sm">Close</span>
              <XMarkIcon className="h-6 w-6" />
            </button>
            <img
              src={zoomedImage}
              alt="Attachment preview"
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default VideoChatPanel;
